import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Badge, Banner, Button, DataTable, Dialog, EmptyState, FilterBar, Inline, InfoButton, PageHeader, Segmented, Stack, t, Text, toast,
} from '@kanbios/ui';
import { useEnvironmentFilter } from '../components/EnvironmentPicker';
import type { fleetState } from '@kanbios/api-client';
import { Shell } from '../components/Shell';
import { useDisplayScope } from '../components/ScopePicker';
import { EnrolmentDot, EnrolmentLegend, EnvironmentLegend, EnvironmentTags, securityRowLabel, securityRowTone, ServerStateLegend, serverStateColumn, serverStateSpec } from '../components/ServerState';
import { type DotSpec, HealthLegend, healthColumn } from '../components/StateColumn';
import { RefusalPage } from '../components/Refusal';
import { AccessCheckStatus, useAccessCheck } from '../components/AccessCheck';
import { BootstrapDrawer } from '../components/BootstrapDrawer';
import { PendingButton } from '../components/PendingButton';
import { MaintenanceDialog } from '../components/MaintenanceDialog';
import { useNarrow } from '../lib/useNarrow';
import { api, unwrap } from '../lib/api';
import { clockTime, relativeTime } from '../lib/format';
import { useSession } from '../lib/session';
import { useFillHeight } from '../lib/useFillHeight';

type Row = fleetState.components['schemas']['ServerListItem'];
/** Client filter value of the servers attached to no client (fleet-state API). */
const UNASSIGNED = '~none';
type ViewId = fleetState.components['schemas']['ServerViewId'];
/** D42: the two lists of the menu. */
export type ServerList = 'monitored' | 'pending' | 'reserve';
const LISTS = {
  monitored: {
    path: '/servers',
    views: ['all', 'incident', 'to_update', 'maintenance', 'unknown', 'ok'],
    // D86: no server in reserve here (they have their own list): no « Réserve » filter.
    facets: ['state', 'environment', 'provider', 'client', 'project', 'team', 'os', 'criticality', 'configuration', 'appSecurity'],
    sort: 'state:asc',
  },
  // D80: the servers kept in reserve (D77), supervised or not.
  reserve: {
    path: '/reserve-servers',
    views: ['all', 'incident', 'to_update', 'maintenance', 'unknown', 'ok'],
    facets: ['state', 'enrolment', 'environment', 'provider', 'team', 'os', 'criticality'],
    sort: 'state:asc',
  },
  pending: {
    path: '/pending-servers',
    views: ['all', 'to_bootstrap', 'to_enrol', 'enrolling', 'failed', 'not_applicable'],
    facets: ['enrolment', 'reserve', 'environment', 'provider', 'client', 'project', 'team', 'os', 'criticality'],
    sort: 'enrolment:asc',
  },
} as const;

function queryString(list: ServerList, scope: string, env: string, view: ViewId, search: string, filters: Record<string, string[]>) {
  const params = new URLSearchParams({ list, scope, env, view });
  if (search) params.set('q', search);
  for (const [key, values] of Object.entries(filters)) if (values.length) params.set(`filter[${key}]`, values.join(','));
  return params;
}

const PHONE_COLUMNS = ['state', 'enrolment', 'name', 'client'];

/**
 * Servers lists (SPEC-003), DSM index page (Layouts › Page types › Index page):
 * PageHeader, views with their counts, then one DataTable that owns search,
 * filters, legend, count, export, density and columns. D42: two entries of the
 * menu, « Serveurs supervisés » (states, configuration, maintenance) and
 * « Serveurs à ajouter à la supervision » (enrolment stage, bootstrap, access
 * check, enrolment); D54: the latter reached from a button of the former.
 */
/** D85: the « D » dot of a server: do the addresses it serves still point to it? */
type DnsCounts = { here: number; elsewhere: number; none: number; other: number };
function dnsSpec(state: 'ok' | 'some' | 'none' | 'unknown', c?: DnsCounts): DotSpec {
  const tone = state === 'ok' ? 'pos' : state === 'some' ? 'warn' : state === 'none' ? 'neg' : 'neutral';
  const total = c ? c.here + c.elsewhere + c.none : 0;
  return {
    tone, label: t(`servers.dns.${state}`),
    description: c ? t('servers.dns.counts', { here: c.here, total }) : t(`servers.dns.rule.${state}`),
  };
}

export function ServersPage({ list: kind = 'monitored' }: { list?: ServerList }) {
  const spec = LISTS[kind];
  // The reserve reads like the supervised list (states, maintenance, configuration).
  const monitoredLike = kind !== 'pending';
  // Binary gigabytes, said « Go » as on the server page's checks.
  const gibibytes = (bytes: number | null) => (bytes === null ? '—'
    : t('servers.gibibytes', { n: new Intl.NumberFormat('fr-FR', { maximumFractionDigits: bytes < 10 * 1024 ** 3 ? 1 : 0 }).format(bytes / 1024 ** 3) }));
  const fill = useFillHeight();
  const narrow = useNarrow();
  const { enums, can } = useSession();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewId>('all');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<string>(spec.sort);
  const scope = useDisplayScope();
  // D73: the global environment filter, production winning.
  const env = useEnvironmentFilter();
  const queryClient = useQueryClient();
  // SPEC-003 grouped maintenance: the selected rows, their refusals after posting.
  const [maintain, setMaintain] = useState<{ rows: Row[]; clear: () => void } | null>(null);
  const [refused, setRefused] = useState<{ server: string; message?: string }[]>([]);
  const [posted, setPosted] = useState(0);
  const [stepUp, setStepUp] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const check = useAccessCheck();

  const filterParam = Object.fromEntries(Object.entries(filters).filter(([, v]) => v.length).map(([k, v]) => [k, v.join(',')]));
  const list = useQuery({
    queryKey: ['servers', kind, scope, env, view, search, filterParam, page, pageSize, sort],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers', { params: { query: { list: kind, scope, env, view, q: search || undefined, page, pageSize, sort, filter: filterParam } } })),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
  // D85: per server, whether the addresses it serves still point to it (DNS read once an hour).
  const dns = useQuery({
    queryKey: ['addresses-dns-by-server', scope],
    queryFn: async () => unwrap(await api.maintenance.GET('/addresses/dns-by-server')),
    enabled: monitoredLike,
    staleTime: 5 * 60_000,
  });
  // D54: how many servers wait to be added to the supervision (the pending list's total), for the header's button.
  // D80: how many servers are kept in reserve, for the header's « Accéder à la réserve (N) ».
  const reserve = useQuery({
    queryKey: ['servers', 'reserve', scope, 'count'],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers', { params: { query: { list: 'reserve', scope, env: 'all', view: 'all', page: 1, pageSize: 1 } } })).total,
    enabled: kind === 'monitored',
    refetchInterval: 30_000,
  });
  const toAdd = useQuery({
    queryKey: ['servers', 'pending', scope, env, 'count'],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers', { params: { query: { list: 'pending', scope, env, view: 'all', page: 1, pageSize: 1 } } })).total,
    enabled: kind === 'monitored',
    refetchInterval: 30_000,
  });

  const labelOf = (facet: string, value: string): string => {
    if (facet === 'state') return enums.states.find(s => s.value === value)?.label ?? value;
    if (facet === 'environment') return enums.environments.find(s => s.value === value)?.label ?? value;
    if (facet === 'client' && value === UNASSIGNED) return t('servers.unassigned');
    if (facet === 'provider') return enums.providers.find(s => s.value === value)?.label ?? value;
    if (facet === 'criticality') return enums.criticalities.find(s => s.value === value)?.label ?? value;
    if (facet === 'configuration') return t(`servers.configuration.${value}`);
    if (facet === 'enrolment') return t(`servers.enrolment.${value}`);
    if (facet === 'appSecurity') return t(`servers.appSecurity.${value}`);
    if (facet === 'reserve') return t(`servers.reserve.${value}`);
    return list.data?.labels[value] ?? value;
  };

  const filterDefs = spec.facets.map(key => ({
    key,
    label: t(`servers.filters.${key}`),
    type: 'multi' as const,
    // D37: the enrolment filter stays on the bar.
    pinned: key === 'enrolment',
    options: Object.entries(list.data?.facets[key] ?? {}).sort(([a], [b]) => labelOf(key, a).localeCompare(labelOf(key, b), 'fr'))
      .map(([value, count]) => ({ value, label: `${labelOf(key, value)} (${count})` })),
  }));

  // D54: the servers to add are reached from « Serveurs supervisés », no longer from the menu.
  const crumbs = kind === 'pending'
    ? [{ label: t('nav.monitoredServers'), href: '/servers' }, { label: t('nav.pendingServers') }]
    : kind === 'reserve'
      ? [{ label: t('nav.monitoredServers'), href: '/servers' }, { label: t('servers.reserveTitle') }]
      : [{ label: t('nav.monitoredServers') }];
  if (list.error) return <Shell crumbs={crumbs}><RefusalPage error={list.error} /></Shell>;

  const counters = list.data?.counters;
  const total = list.data?.total ?? 0;
  const exportCsv = () => {
    window.location.assign(`/api/fleet-state/servers/export?format=csv&${queryString(kind, scope, env, view, search, filters).toString()}`);
    toast({ title: t('servers.exported', { n: total }), tone: 'info' });
  };

  // SPEC-003 « Largeur téléphone »: État, Serveur, Environnement; the others hidden by default.
  const columns = [
    ...(kind === 'pending' ? [{
      // D42: the stage said in words, the list's first idea.
      key: 'enrolment', width: narrow ? 200 : 230, sortable: true,
      label: (
        <Inline gap={4} align="center" wrap={false}>
          <span>{t('servers.columns.enrolment')}</span>
          <InfoButton label={t('servers.enrolmentLegend')} title={t('servers.enrolmentLegendTitle')} size="sm"><EnrolmentLegend /></InfoButton>
        </Inline>
      ),
      render: (r: Row) => <Inline gap={8} align="center" wrap={false}><EnrolmentDot stage={r.enrolment} /><span>{t(`servers.enrolment.${r.enrolment}`)}</span></Inline>,
    }] : [
      // D53: the state first, a dot only (note and cause in its tooltip), the legend in the header.
      // D71: the server's health, then its applications' (« — » without declared application).
      // D74: one letter each (S, A), the word in the header's abbr and the legend's name.
      serverStateColumn<Row>(enums, r => ({ state: r.state, reason: r.stateReason, note: r.stateNote }), { securityRow: monitoredLike, title: t('servers.columns.serverHealth'), abbreviation: t('servers.columns.serverHealthShort') }),
      healthColumn<Row>({
        key: 'applicationState', title: t('servers.columns.applicationHealth'), abbreviation: t('servers.columns.applicationHealthShort'),
        legendLabel: t('servers.legendOf', { column: t('servers.columns.applicationHealth') }), legendTitle: t('servers.applicationLegendTitle'),
        legend: <ServerStateLegend />,
        spec: r => (r.applicationState ? serverStateSpec(enums, r.applicationState, r.applicationReason) : null),
      }),
      // D85: its domains — green when every address points to it, orange when one no longer does, red when none does.
      healthColumn<Row>({
        key: 'dns', title: t('servers.columns.dns'), abbreviation: t('servers.columns.dnsShort'),
        legendLabel: t('servers.legendOf', { column: t('servers.columns.dns') }), legendTitle: t('servers.dnsLegendTitle'),
        legend: <HealthLegend items={(['ok', 'some', 'none', 'unknown'] as const).map(k => dnsSpec(k))} />,
        spec: r => {
          const c = dns.data?.servers[r.name];
          if (!c) return null;
          const gone = c.elsewhere + c.none;
          if (!gone && !c.here) return c.other ? dnsSpec('unknown') : null;
          return gone === 0 ? dnsSpec('ok', c) : c.here === 0 ? dnsSpec('none', c) : dnsSpec('some', c);
        },
      }),
    ]),
    // D74: the environments of its declared deployments in their own column « E », its legend behind the « i ».
    ...([{
      key: 'environmentTypes', width: 96, minWidth: 96, sortable: false, resizable: false, pinned: 'left' as const,
      label: (
        <Inline gap={4} align="center" wrap={false}>
          <abbr title={t('servers.columns.environments')}>{t('servers.columns.environmentsShort')}</abbr>
          <InfoButton label={t('servers.legendOf', { column: t('servers.columns.environments') })} title={t('servers.columns.environments')} size="sm"><EnvironmentLegend /></InfoButton>
        </Inline>
      ),
      accessor: (r: Row) => `${r.spare ? `${t('spare.badge')} ` : ''}${r.environmentTypes.join(' ')}`,
      render: (r: Row) => <EnvironmentTags types={r.environmentTypes} spare={r.spare} />,
    }]),
    { key: 'name', label: t('servers.columns.name'), pinned: 'left' as const, width: 250, minWidth: 190, sortable: true, accessor: (r: Row) => r.name,
      render: (r: Row) => <Text variant="code" truncate title={r.name}>{r.name}</Text> },
    // D63: client and project in their own columns.
    { key: 'client', label: t('servers.columns.client'), width: 170, accessor: (r: Row) => r.client ?? t('servers.noData') },
    { key: 'project', label: t('servers.columns.project'), width: 180, grow: true, accessor: (r: Row) => r.project ?? '—' },
    { key: 'applications', label: t('servers.columns.applications'), hidden: true, render: (r: Row) => r.applications.length > 2 ? `${r.applications.slice(0, 2).join(', ')} ${t('servers.moreApps', { n: r.applications.length - 2 })}` : r.applications.join(', ') },
    { key: 'ip', label: t('servers.columns.ip'), mono: true, width: 140 },
    { key: 'os', label: t('servers.columns.os'), width: 140, render: (r: Row) => r.os ?? t('servers.noData') },
    // D87: in reserve, what each server offers, as its agent reports it (« — » before its enrolment).
    ...(kind === 'reserve' ? [
      { key: 'cpus', label: t('servers.columns.cpus'), width: 90, align: 'right' as const, accessor: (r: Row) => r.capacity.cpus ?? '—' },
      { key: 'memory', label: t('servers.columns.memory'), width: 100, align: 'right' as const, accessor: (r: Row) => gibibytes(r.capacity.memoryBytes) },
      { key: 'storage', label: t('servers.columns.storage'), width: 110, align: 'right' as const, accessor: (r: Row) => gibibytes(r.capacity.storageBytes) },
    ] : []),
    // D63: the provider and its region in one column (« OVHcloud · GRA »).
    { key: 'provider', label: t('servers.columns.hosting'), width: 170, sortable: true, accessor: (r: Row) => [labelOf('provider', r.provider), r.region].filter(Boolean).join(' · ') },
    { key: 'team', label: t('servers.columns.team'), hidden: true },
    { key: 'criticality', label: t('servers.columns.criticality'), hidden: true, render: (r: Row) => labelOf('criticality', r.criticality) },
    ...(monitoredLike ? [
      // D30: what an update of the configuration would bring.
      { key: 'configuration', label: t('servers.columns.configuration'), width: 160, render: (r: Row) => (r.configuration === 'update_failed'
        ? <Badge tone="neg" size="sm">{t('servers.configuration.update_failed')}</Badge>
        : r.configuration === 'outdated'
        ? <Badge tone="warn" size="sm">{t('servers.configuration.outdated')}</Badge>
        : <span>{t(`servers.configuration.${r.configuration}`)}</span>) },
    ] : []),
  ].map(c => (narrow && !PHONE_COLUMNS.includes(c.key) ? { ...c, hidden: true } : c));

  const views = list.data?.views;
  // DSM Segmented counts: a state that calls for action inks its count (never a fill) and says it
  // in words, only when it holds servers; Unknown, OK and Tout keep a plain count.
  const VIEW_TONES: Record<string, 'neg' | 'warn' | 'info'> = { incident: 'neg', to_update: 'warn', maintenance: 'info' };
  const viewCount = (id: string) => {
    const n = views?.[id];
    if (n === undefined) return {};
    const tone = VIEW_TONES[id];
    return tone && n > 0 ? { count: n, countTone: tone, countLabel: t(`servers.viewLabel.${id}`, { n }) } : { count: n };
  };
  return (
    <Shell crumbs={crumbs}>
      <Stack gap={24}>
        <PageHeader
          title={t(kind === 'pending' ? 'servers.pendingTitle' : kind === 'reserve' ? 'servers.reserveTitle' : 'servers.title')}
          status={counters ? <Badge tone="neutral">{counters.total}</Badge> : undefined}
          subtitle={kind === 'pending'
            ? t('servers.pendingSubtitle')
            : kind === 'reserve' ? t('servers.reserveSubtitle')
            : list.data ? t('servers.subtitleFresh', { when: relativeTime(list.data.generatedAt) }) : t('servers.subtitle')}
          // SPEC-003: « Importer » is the primary action until the server ordering module exists.
          // D30: acting on servers starts here — bootstrap, access check, then « Importer » (the enrolment wizard).
          actions={kind === 'pending' && can('server.enrol') ? (
            <Inline gap={8}>
              <Button variant="secondary" icon="add_link" onClick={() => setBootstrapping(true)}>{t('servers.bootstrap')}</Button>
              <PendingButton variant="secondary" icon="sync" pending={check.running} onClick={check.run}>{t('servers.checkAccess')}</PendingButton>
              <Button icon="download" onClick={() => navigate('/servers/import')}>{t('servers.import')}</Button>
            </Inline>
          ) : kind === 'monitored' && ((reserve.data ?? 0) > 0 || (toAdd.data ?? 0) > 0) ? (
            <Inline gap={8}>
              {/* D80: the reserve, left of the servers to add. */}
              {(reserve.data ?? 0) > 0 && <Button variant="secondary" icon="inventory_2" onClick={() => navigate('/reserve-servers')}>{t('servers.toReserve', { n: reserve.data! })}</Button>}
              {(toAdd.data ?? 0) > 0 && <Button variant="secondary" icon="add_circle" onClick={() => navigate('/pending-servers')}>{t('servers.toAdd', { n: toAdd.data! })}</Button>}
            </Inline>
          ) : undefined}
        />
        <AccessCheckStatus feedback={check.feedback} />
        {refused.length > 0 && (
          <Alert tone="warn" title={t('maintenance.bulkPartial', { n: posted, total: posted + refused.length })}>
            {refused.map(r => `${r.server} : ${r.message ?? ''}`).join(' · ')}
          </Alert>
        )}
        {list.data?.stale && (
          <Banner tone="warn" title={t('servers.stale.title')}>{t('servers.stale.body', { when: clockTime(list.data.generatedAt) })}</Banner>
        )}
        <Segmented
          label={t('servers.views')}
          size="sm"
          value={view}
          onChange={id => { setView(id as ViewId); setPage(1); }}
          // D42: each list its own views, the states or the enrolment stages.
          items={enums.serverViews.filter(v => (spec.views as readonly string[]).includes(v.value)).map(v => ({ id: v.value, label: v.label, ...viewCount(v.value) }))}
        />
        <div ref={fill[0]}>
          <DataTable
            // The columns' default visibility is read once: the table starts again when the width crosses 600 px.
            key={narrow ? 'phone' : 'wide'}
            height={fill[1]}
            label={t('servers.table')}
            columns={columns}
            rows={list.data?.items ?? []}
            rowKey="name"
            // D63: a pending security update tints the whole row, said in the state column's legend and to screen readers.
            rowTone={monitoredLike ? (r: Row) => securityRowTone(Boolean(r.security)) : undefined}
            rowToneLabel={monitoredLike ? (r: Row) => securityRowLabel(Boolean(r.security)) : undefined}
            selectable={!narrow && ((monitoredLike && can('maintenance.create')) || can('server.enrol'))}
            bulkActions={(rows: Row[], clear: () => void) => (
              <Inline gap={8}>
                {monitoredLike && can('maintenance.create') && <Button size="sm" icon="build" onClick={() => setMaintain({ rows, clear })}>{t('maintenance.bulk', { n: rows.length })}</Button>}
                {/* D30: the wizard enrols the new ones and updates the configuration of the others, reviewed at the Accès step. */}
                {can('server.enrol') && <Button size="sm" variant={kind === 'pending' ? 'primary' : 'secondary'} icon="system_update_alt" onClick={() => navigate(`/servers/import?servers=${encodeURIComponent(rows.map(r => r.name).join(','))}`)}>{t(kind === 'pending' ? 'servers.enrolOnly' : 'servers.updateSelected', { n: rows.length })}</Button>}
              </Inline>
            )}
            manual
            totalCount={total}
            // Facets and search are served by the API (counts over the whole fleet, not the page):
            // the FilterBar owns the query, inside the table's toolbar.
            toolbarStart={
              <FilterBar
                label={t('servers.filters.label')}
                filters={filterDefs}
                value={filters}
                onChange={v => { setFilters(v as Record<string, string[]>); setPage(1); }}
                search={search}
                onSearchChange={q => { setSearch(q); setPage(1); }}
                searchPlaceholder={t('servers.search')}
              />
            }
            toolbarSearch={false}
            toolbarFilters={false}
            toolbarDensity={!narrow}
            toolbarColumns={!narrow}
            density="compact"
            loading={list.isPending}
            defaultSort={{ key: kind === 'pending' ? 'enrolment' : 'state', dir: 'asc' }}
            onQueryChange={q => { if (q.sort) { setSort(`${q.sort.key}:${q.sort.dir}`); setPage(1); } }}
            onRowClick={(row: Row) => navigate(`/servers/${encodeURIComponent(row.name)}`)}
            rowState={(row: Row) => (row.archived ? 'dimmed' : null)}
            toolbarActions={!narrow && can('server.export') ? <Button variant="secondary" size="sm" icon="download" onClick={exportCsv} disabled={total === 0}>{t('servers.export', { n: total })}</Button> : undefined}
            empty={kind === 'pending'
              ? <EmptyState icon="task_alt" title={t('servers.pendingEmpty.title')}>{t('servers.pendingEmpty.body')}</EmptyState>
              : kind === 'reserve' ? <EmptyState icon="inventory_2" title={t('servers.reserveEmpty.title')}>{t('servers.reserveEmpty.body')}</EmptyState>
              : <EmptyState icon="dns" title={t('servers.empty.title')}>{t('servers.empty.body')}</EmptyState>}
            pagination={{ page, pageSize, total, onPageChange: setPage, pageSizes: [25, 50, 100], onPageSizeChange: size => { setPageSize(size); setPage(1); } }}
          />
        </div>
      </Stack>
      <MaintenanceDialog
        open={maintain !== null}
        targets={(maintain?.rows ?? []).map(r => ({ name: r.name, environment: r.environment, team: r.team }))}
        environmentLabel={env => labelOf('environment', env)}
        onClose={() => setMaintain(null)}
        onDone={({ rejected }) => {
          const n = (maintain?.rows.length ?? 0) - rejected.length;
          setPosted(n);
          setRefused(rejected.map(r => ({ server: r.server, message: r.message })));
          maintain?.clear();
          void queryClient.invalidateQueries({ queryKey: ['servers'] });
        }}
        onStepUp={() => { setMaintain(null); setStepUp(true); }}
      />
      <Dialog
        open={stepUp}
        onClose={() => setStepUp(false)}
        title={t('settings.enrolment.stepUp.title')}
        description={t('maintenance.stepUp')}
        confirmLabel={t('settings.enrolment.stepUp.confirm')}
        onConfirm={() => window.location.assign(`/api/auth/session/step-up?returnTo=${encodeURIComponent(spec.path)}`)}
      />
      <BootstrapDrawer open={bootstrapping} onClose={() => setBootstrapping(false)} onCheck={check.run} checking={check.running} />
    </Shell>
  );
}
